import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { startOfDay, endOfDay } from 'date-fns';
import dayjs from 'dayjs';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { DateSelector, RelativelyPositionedSearchFilter, SubHeader, Table } from '@/components';
import {
  getSavedCurrentPage,
  getSavedRowsPerPage,
  getSavedSortModel,
  onQueryError,
  onSelectRow,
  saveSortModelToLocalStorage,
  FinancialClosureItem,
  useOrders,
} from '@/core';
import { DefaultCell } from '@/core/renderCells';
import { OrderTableHeader } from '@/features';
import palette from '@/theme/palette';

const FINANCIAL_CLOSURE_PAGE_KEY = 'financialClosure';

export const PageFinancialClosure = () => {
  const { t } = useTranslation();
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | undefined>({
    from: dayjs().startOf('year').toDate(),
    to: dayjs().endOf('year').toDate(),
  });
  const [selectedDateRangeName, setSelectedDateRangeName] = useState<string | undefined>(
    t('FINANCIAL_CLOSURE.THIS_YEAR'),
  );
  const [dateFilterPopoverAnchor, setDateFilterPopoverAnchor] = useState<HTMLElement | undefined>(
    undefined,
  );

  //pagination states
  const [currentPage, setCurrentPage] = useState<number>(
    getSavedCurrentPage(FINANCIAL_CLOSURE_PAGE_KEY),
  );
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    getSavedRowsPerPage(FINANCIAL_CLOSURE_PAGE_KEY),
  );

  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [sortModel, setSortModel] = useState<GridSortModel>(
    getSavedSortModel(FINANCIAL_CLOSURE_PAGE_KEY).length > 0
      ? getSavedSortModel(FINANCIAL_CLOSURE_PAGE_KEY)
      : [{ field: 'total_taxed_revenue_czk', sort: 'desc' }],
  );

  const {
    queries: { useOrdersProductExport },
    mutations: { exportOrdersProductXlsxMutation },
  } = useOrders();

  const activeSortItem = sortModel[0];
  const [exportEnabled, setExportEnabled] = useState(false);

  const {
    data: tableData,
    status,
    refetch,
  } = useOrdersProductExport(
    selectedDateRange?.from,
    selectedDateRange?.to,
    activeSortItem?.field,
    activeSortItem?.sort ?? undefined,
    rowsPerPage,
    currentPage,
  );
  const financialClosureItems = tableData?.results;
  const paginationTotal = tableData?.total_results ?? 0;

  const { data: exportData, refetch: refetchExport } = useOrdersProductExport(
    selectedDateRange?.from,
    selectedDateRange?.to,
    activeSortItem?.field,
    activeSortItem?.sort ?? undefined,
    0,
    0,
    exportEnabled,
  );
  const allExportItems = exportData?.results;

  const filteredFinancialClosureItems = financialClosureItems?.filter((item) =>
    searchInputValue.length > 0
      ? item.name.toLowerCase().includes(searchInputValue.toLowerCase())
      : true,
  );
  const displayTotal = paginationTotal;

  const handleDateFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setDateFilterPopoverAnchor(event.currentTarget);
  };

  const handleCustomRangeChange = (range?: DateRange) => {
    const adjustedRange: DateRange = {
      from: range?.from ? startOfDay(range.from) : undefined,
      to: range?.to ? endOfDay(range.to) : undefined,
    };
    setSelectedDateRange(adjustedRange);
    setSelectedDateRangeName(t('FINANCIAL_CLOSURE.CUSTOM'));
  };

  const handleCancelAction = () => {
    setSelectedRows([]);
  };

  const doPrint = () => {
    const styleTag = document.createElement('style');
    styleTag.innerHTML = `
      @media print {
        @page { size: A4 landscape; margin: 10mm; }
        .MuiAppBar-root,
        .MuiDrawer-root { display: none !important; }
        [data-print="hide"] { display: none !important; }
        [data-print="only"] { display: block !important; }
        .MuiStack-root {
          margin-left: 0 !important;
          margin-top: 0 !important;
          width: 100% !important;
        }
        body { margin: 0; }
      }
    `;
    document.head.appendChild(styleTag);
    window.print();
    document.head.removeChild(styleTag);
  };

  const [pendingPrint, setPendingPrint] = useState(false);

  useEffect(() => {
    if (pendingPrint && allExportItems) {
      doPrint();
      setPendingPrint(false);
    }
  }, [pendingPrint, allExportItems]);

  const handlePrint = () => {
    setPendingPrint(true);
    setExportEnabled(true);
    refetchExport();
  };

  const handleExportXlsx = () => {
    exportOrdersProductXlsxMutation.mutate(
      { since: selectedDateRange?.from, until: selectedDateRange?.to },
      {
        onSuccess: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'financni-uzaverka.xlsx';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        },
        onError: () => onQueryError({ errorMessage: t('FINANCIAL_CLOSURE.EXPORT_EXCEL_FAILURE') }),
      },
    );
  };

  useEffect(() => {
    if (!dateFilterPopoverAnchor) refetch();
  }, [selectedDateRange, refetch, dateFilterPopoverAnchor]);

  const timePeriods = [
    {
      label: t('FINANCIAL_CLOSURE.TODAY'),
      range: { from: dayjs().startOf('day').toDate(), to: dayjs().endOf('day').toDate() },
    },
    {
      label: t('FINANCIAL_CLOSURE.YESTERDAY'),
      range: {
        from: dayjs().subtract(1, 'day').startOf('day').toDate(),
        to: dayjs().subtract(1, 'day').endOf('day').toDate(),
      },
    },
    {
      label: t('FINANCIAL_CLOSURE.THIS_MONTH'),
      range: { from: dayjs().startOf('month').toDate(), to: dayjs().endOf('month').toDate() },
    },
    {
      label: t('FINANCIAL_CLOSURE.LAST_MONTH'),
      range: {
        from: dayjs().subtract(1, 'month').startOf('month').toDate(),
        to: dayjs().subtract(1, 'month').endOf('month').toDate(),
      },
    },
    {
      label: t('FINANCIAL_CLOSURE.THIS_YEAR'),
      range: { from: dayjs().startOf('year').toDate(), to: dayjs().endOf('year').toDate() },
    },
    {
      label: t('FINANCIAL_CLOSURE.CUSTOM'),
      range: { from: undefined, to: undefined },
    },
  ];

  const columns: GridColDef<FinancialClosureItem>[] = [
    {
      field: 'plu_code',
      headerName: t('FINANCIAL_CLOSURE.PLU'),
      flex: 1,
      renderCell: (params) => <DefaultCell value={params.value} />,
    },
    {
      field: 'name',
      headerName: t('FINANCIAL_CLOSURE.NAME'),
      flex: 4,
      renderCell: (params) => (
        <DefaultCell value={params.value?.toString().replace(/:/g, ', ')} tooltip />
      ),
    },
    {
      field: 'is_takeout',
      headerName: t('FINANCIAL_CLOSURE.TYPE'),
      flex: 1,
      renderCell: (params) => (
        <DefaultCell
          value={
            params.value === null
              ? ''
              : params.value
                ? t('FINANCIAL_CLOSURE.TAKEOUT')
                : t('FINANCIAL_CLOSURE.ON_SPOT')
          }
        />
      ),
    },
    {
      field: 'total_sold',
      headerName: t('FINANCIAL_CLOSURE.ITEM_COUNT'),
      flex: 1,
      renderCell: (params) => <DefaultCell value={params.value} />,
    },
    {
      field: 'unit_price_with_tax',
      headerName: t('FINANCIAL_CLOSURE.ITEM_PRICE_WITH_TAX'),
      flex: 1,
      renderCell: (params) => <DefaultCell value={params.value} />,
      sortable: false,
    },
    {
      field: 'tax_value',
      headerName: t('FINANCIAL_CLOSURE.TAX'),
      flex: 1,
      renderCell: (params) => <DefaultCell value={params.value} />,
    },
    {
      field: 'total_revenue_czk',
      headerName: t('FINANCIAL_CLOSURE.TAX_BASE_IN_CZK'),
      flex: 1,
      renderCell: (params) => <DefaultCell value={params.value} />,
    },
    {
      field: 'tax_in_czk',
      headerName: t('FINANCIAL_CLOSURE.TAX_IN_CZK'),
      flex: 1,
      renderCell: (params) => <DefaultCell value={params.value} />,
      sortable: false,
    },
    {
      field: 'total_taxed_revenue_czk',
      headerName: t('FINANCIAL_CLOSURE.TOTAL_IN_CZK'),
      flex: 1,
      renderCell: (params) => <DefaultCell value={params.value} />,
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Box data-print="hide">
        <SubHeader />
      </Box>
      <Stack direction="row" alignItems="center" gap={1} padding={3} pt={6} data-print="hide">
        <Typography mr={2}>{t('FINANCIAL_CLOSURE.TIME_PERIOD')}</Typography>
        {timePeriods.map((period) => (
          <Button
            variant="outlined"
            size="small"
            key={period.label}
            sx={{
              paddingY: 1,
              fontWeight: 'normal',
              color: palette.text.primary,
              border: `1px solid ${selectedDateRangeName === period.label ? palette.primary.main : palette.border.default}`,
              borderRadius: '5px',
            }}
            onClick={(event) => {
              if (period.label === t('FINANCIAL_CLOSURE.CUSTOM')) {
                handleDateFilterClick(event);
              } else {
                setDateFilterPopoverAnchor(undefined);
                setSelectedDateRangeName(period.label);
                setSelectedDateRange(period.range);
              }
            }}
          >
            {period.label}
          </Button>
        ))}
      </Stack>
      <Box data-print="hide">
        <RelativelyPositionedSearchFilter
          inputValue={searchInputValue}
          setInputValue={setSearchInputValue}
          placeholder={t('FINANCIAL_CLOSURE.SEARCH_PLACEHOLDER')}
          sx={{ marginBottom: '-40px' }}
        />
      </Box>
      <Box data-print="hide">
        <Table
          key={`${searchInputValue}_${currentPage}_${rowsPerPage}_${JSON.stringify(financialClosureItems)}`}
          rows={[
            ...(filteredFinancialClosureItems ?? []),
            ...(filteredFinancialClosureItems && filteredFinancialClosureItems.length > 0
              ? [
                  {
                    id: -1,
                    plu_code: t('FINANCIAL_CLOSURE.TOTAL_SALES'),
                    name: '',
                    is_takeout: null,
                    total_sold: financialClosureItems?.reduce(
                      (acc, item) => acc + Number(item.total_sold),
                      0,
                    ),
                    unit_price_with_tax: '',
                    tax: '',
                    total_revenue_czk: financialClosureItems
                      ?.reduce((acc, item) => acc + Number(item.total_revenue_czk), 0)
                      .toFixed(2),
                    tax_in_czk: financialClosureItems
                      ?.reduce((acc, item) => acc + Number(item.tax_in_czk), 0)
                      .toFixed(2),
                    total_taxed_revenue_czk: financialClosureItems
                      ?.reduce((acc, item) => acc + Number(item.total_taxed_revenue_czk), 0)
                      .toFixed(2),
                  },
                ]
              : []),
          ]}
          columns={columns}
          tableId={FINANCIAL_CLOSURE_PAGE_KEY}
          isDraggable={false}
          loading={status === 'pending'}
          selectedRows={selectedRows}
          onSelectRow={(selectedRowId) => onSelectRow(selectedRowId, setSelectedRows)}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model);
            saveSortModelToLocalStorage(FINANCIAL_CLOSURE_PAGE_KEY, model);
          }}
          pagination
          isCheckBox={false}
          paginationOptions={{
            currentPage,
            rowsPerPage,
            totalRows: paginationTotal,
            rowCount: paginationTotal,
            rowsPerPageOptions: [15, 25, 50, 100],
            onPageSizeChange: (newPageSize: number) => {
              setRowsPerPage(newPageSize);
            },
            onPageChange: (newPage: number) => {
              setCurrentPage(newPage);
            },
            pageSize: rowsPerPage,
            page: currentPage,
          }}
          ToolBar={(props) => (
            <OrderTableHeader
              {...props}
              rows={financialClosureItems?.map((row) => row.id) || []}
              count={displayTotal}
              selectedRows={selectedRows}
              onDelete={() => {}}
              onCancel={handleCancelAction}
              onExport={handlePrint}
              onExportXlsx={handleExportXlsx}
            />
          )}
          sx={{
            '& .MuiDataGrid-row--lastVisible': {
              '& .MuiDataGrid-cell': {
                backgroundColor: 'surface.lightest',
              },
              '& .MuiTypography-root': {
                fontSize: '16px',
              },
              '& .MuiDataGrid-cell:nth-of-type(3)': {
                overflow: 'visible',
                zIndex: 1,
                '& .MuiTypography-root': {
                  color: 'primary.main',
                  fontWeight: 'bold',
                  fontSize: '20px',
                },
              },
              '& .MuiDataGrid-cell:nth-last-of-type(3) .MuiTypography-root': {
                color: 'primary.main',
                fontWeight: 'bold',
                fontSize: '20px',
              },
            },
          }}
          getRowId={(row) => `${row?.id} ${row?.variant_id} ${row?.name}`}
        />
      </Box>
      {/* Print-only table — rendered as a plain HTML table, shown only during print */}
      <Box data-print="only" sx={{ display: 'none' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.field}
                  style={{
                    border: '1px solid #ccc',
                    padding: '4px 6px',
                    textAlign: 'left',
                    backgroundColor: '#f5f5f5',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col.headerName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allExportItems?.map((row) => (
              <tr key={`${row.id} ${row.variant_id} ${row.name}`}>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}>{row.plu_code}</td>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}>
                  {row.name?.toString().replace(/:/g, ', ')}
                </td>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}>
                  {row.is_takeout ? t('FINANCIAL_CLOSURE.TAKEOUT') : t('FINANCIAL_CLOSURE.ON_SPOT')}
                </td>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}>{row.total_sold}</td>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}>
                  {row.unit_price_with_tax}
                </td>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px', width: '40px' }}>
                  {row.tax_value}
                </td>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}>
                  {row.total_revenue_czk}
                </td>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}>{row.tax_in_czk}</td>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}>
                  {row.total_taxed_revenue_czk}
                </td>
              </tr>
            ))}
            {allExportItems && allExportItems.length > 0 && (
              <tr style={{ fontWeight: 'bold', borderTop: '2px solid #000' }}>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}>
                  {t('FINANCIAL_CLOSURE.TOTAL_SALES')}
                </td>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}></td>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}></td>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}>
                  {allExportItems.reduce((acc, item) => acc + Number(item.total_sold), 0)}
                </td>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}></td>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}></td>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}>
                  {allExportItems
                    .reduce((acc, item) => acc + Number(item.total_revenue_czk), 0)
                    .toFixed(2)}
                </td>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}>
                  {allExportItems
                    .reduce((acc, item) => acc + Number(item.tax_in_czk), 0)
                    .toFixed(2)}
                </td>
                <td style={{ border: '1px solid #ccc', padding: '3px 6px' }}>
                  {allExportItems
                    .reduce((acc, item) => acc + Number(item.total_taxed_revenue_czk), 0)
                    .toFixed(2)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>
      <DateSelector
        rangeState={[selectedDateRange, handleCustomRangeChange]}
        dateRangeFilterPopoverProps={[dateFilterPopoverAnchor, setDateFilterPopoverAnchor]}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      />
    </Box>
  );
};
